import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Textarea } from '../ui/textarea';
import { apiService, ApiError } from '../../services/api';
import { formatDate } from '../../lib/utils';
import { DocumentMetadata, DocumentWithReview, DocumentReviewStatus, ReviewComment } from '../../types/api';
import { useConvexAuth, useQuery } from 'convex/react';
import { api } from '../../convex/api';
import {
  Upload,
  File,
  Trash2,
  Download,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ArrowUpDown,
  Info,
  X,
  Search,
  Eye,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  MoreVertical,
} from 'lucide-react';

export function DocumentManager({ isActive }: { isActive?: boolean }) {
  const [documents, setDocuments] = useState<DocumentMetadata[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [uploadWarning, setUploadWarning] = useState('');
  const [documentName, setDocumentName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [validatingDocId, setValidatingDocId] = useState<string | null>(null);
  const [validationResult, setValidationResult] = useState<{
    documentId: string;
    status: 'VALID' | 'INVALID';
    errors: string[];
    notes?: string;
  } | null>(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<{ url: string; name: string; mimeType: string } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [expandedComments, setExpandedComments] = useState<Record<string, boolean>>({});
  const [feedbackDoc, setFeedbackDoc] = useState<DocumentWithReview | null>(null);
  const [feedbackStatus, setFeedbackStatus] = useState<'approved' | 'rejected'>('rejected');
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');
  const [loadingDocId, setLoadingDocId] = useState<string | null>(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // Convex real-time document reviews
  const { isAuthenticated: isConvexAuth } = useConvexAuth();
  const convexReviewsQuery = useQuery(
    api.document_reviews.listForAuthenticatedUser,
    isConvexAuth ? {} : 'skip'
  ) as Array<{
    document_id: string;
    document_name: string;
    status: string;
    review_comments: Array<{
      id: string;
      comment: string;
      status: 'open' | 'closed';
      created_at: number;
      closed_at?: number;
      reviewer_id: string;
    }>;
  }> | null | undefined;
  const convexReviews = convexReviewsQuery ?? [];

  const documentsWithReviews: DocumentWithReview[] = useMemo(() => {
    if (!convexReviews.length) return documents;
    return documents.map(doc => {
      const convexReview = convexReviews.find(
        r => r.document_id === doc.id || r.document_name === doc.documentName
      );
      if (!convexReview) return doc;
      return {
        ...doc,
        reviewStatus: convexReview.status as DocumentReviewStatus,
        reviewComments: convexReview.review_comments as ReviewComment[],
      };
    });
  }, [documents, convexReviews]);

  const applyValidationFeedback = (result: {
    status: 'VALID' | 'INVALID';
    errors: string[];
    notes?: string;
  }) => {
    setUploadError('');
    setUploadSuccess('');
    setUploadWarning('');
    if (result.status !== 'VALID') {
      const message = result.notes?.trim();
      setUploadError(message || 'Document validation failed');
    }
  };

  const fetchDocuments = async () => {
    try {
      setIsLoading(true);
      setUploadError('');
      const docs = await apiService.listDocuments();
      setDocuments(docs);
      setHasLoadedOnce(true);
    } catch (err) {
      setUploadError(err instanceof ApiError ? err.message : 'Failed to load documents');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    
    // Store the selected file
    setSelectedFile(file);
    setValidationResult(null); // Clear previous validation result
    setUploadError('');
    setUploadSuccess('');
    setUploadWarning('');

    // Basic file validation
    if (!['image/jpeg', 'image/jpg', 'application/pdf'].includes(file.type)) {
      setUploadError('Only JPG and PDF files are allowed');
      event.target.value = '';
      setSelectedFile(null);
      return;
    }

    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadError(`File size must be less than 2MB. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
      event.target.value = '';
      setSelectedFile(null);
      return;
    }

    setUploadSuccess(`File selected: ${file.name}`);
    
    // Auto-validate after file is selected
    setTimeout(() => {
      autoValidateDocument(file);
    }, 100);
  };

  const autoValidateDocument = async (file: File) => {
    if (!file || !documentName.trim()) {
      return;
    }

    try {
      setValidatingDocId('current-file');
      setUploadError('');
      setUploadSuccess('');
      setUploadWarning('');

      const result = await apiService.validateDocument(file, documentName.trim());
      
      setValidationResult({
        documentId: 'current-file',
        status: result.status,
        errors: result.errors,
        notes: result.notes,
      });
      applyValidationFeedback(result);
    } catch (err) {
      setUploadError(err instanceof ApiError ? err.message : 'Failed to validate document');
    } finally {
      setValidatingDocId(null);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    const files = event.dataTransfer.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    
    // Store the selected file
    setSelectedFile(file);
    setValidationResult(null);
    setUploadError('');
    setUploadSuccess('');
    setUploadWarning('');

    // Basic file validation
    if (!['image/jpeg', 'image/jpg', 'application/pdf'].includes(file.type)) {
      setUploadError('Only JPG and PDF files are allowed');
      setSelectedFile(null);
      return;
    }

    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadError(`File size must be less than 2MB. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
      setSelectedFile(null);
      return;
    }

    setUploadSuccess(`File selected: ${file.name}`);
    
    // Auto-validate after file is selected
    setTimeout(() => {
      autoValidateDocument(file);
    }, 100);
  };

  const handleUploadClick = async () => {
    if (!selectedFile || !documentName.trim()) {
      setUploadError('Please select a file and enter a document name');
      return;
    }

    try {
      setIsUploading(true);
      setUploadError('');
      setUploadSuccess('');
      setUploadWarning('');

      const result = await apiService.uploadDocument(selectedFile, documentName.trim());
      
      if (result.success) {
        setUploadSuccess(`${documentName} uploaded successfully!`);
        setUploadWarning('');
        setDocumentName('');
        setSelectedFile(null);
        setValidationResult(null);
        // Refresh documents list
        await fetchDocuments();
        // Clear the file input
        const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
        // Close the dialog after a short delay
        setTimeout(() => {
          setShowUploadDialog(false);
          setUploadSuccess('');
        }, 1500);
      } else {
        setUploadError('Failed to upload document');
      }
    } catch (err) {
      if (err instanceof ApiError && err.details) {
        try {
          let errorData: any = err.details;
          if (typeof errorData === 'string') {
            errorData = JSON.parse(errorData);
          }
          
          if (errorData && errorData.errors && Array.isArray(errorData.errors)) {
            const errorList = errorData.errors
              .map((error: string, index: number) => `${index + 1}. ${error}`)
              .join('\n');
            setUploadError(`Document validation failed:\n\n${errorList}`);
          } else if (errorData && errorData.validationErrors && Array.isArray(errorData.validationErrors)) {
            const errorList = errorData.validationErrors
              .map((error: string, index: number) => `${index + 1}. ${error}`)
              .join('\n');
            setUploadError(`Document validation failed:\n\n${errorList}`);
          } else if (Array.isArray(errorData)) {
            const errorList = errorData
              .map((error: string, index: number) => `${index + 1}. ${error}`)
              .join('\n');
            setUploadError(`Document validation failed:\n\n${errorList}`);
          } else {
            setUploadError(err instanceof ApiError ? err.message : 'Failed to upload document');
          }
        } catch (parseError) {
          setUploadError(err instanceof ApiError ? err.message : 'Failed to upload document');
        }
      } else {
        setUploadError(err instanceof ApiError ? err.message : 'Failed to upload document');
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    try {
      const result = await apiService.deleteDocument(documentId);
      
      if (result.success) {
        setUploadSuccess('Document deleted successfully');
        // Refresh documents list
        await fetchDocuments();
      } else {
        setUploadError('Failed to delete document');
      }
    } catch (err) {
      setUploadError(err instanceof ApiError ? err.message : 'Failed to delete document');
    }
  };

  const handleDownloadDocument = async (document: DocumentWithReview) => {
    try {
      setLoadingDocId(document.id);
      const blob = await apiService.downloadDocument(document.id);
      
      const getExtensionFromMimeType = (mimeType: string): string => {
        const mimeToExt: Record<string, string> = {
          'image/jpeg': '.jpg',
          'image/jpg': '.jpg',
          'image/png': '.png',
          'image/gif': '.gif',
          'image/webp': '.webp',
          'application/pdf': '.pdf',
        };
        return mimeToExt[mimeType.toLowerCase()] || '.jpg';
      };

      const correctExtension = getExtensionFromMimeType(document.mimeType);
      const baseNameWithoutExt = document.documentName.replace(/\.[^/.]+$/, '');
      const correctedFilename = `${baseNameWithoutExt}${correctExtension}`;
      
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = correctedFilename;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setUploadSuccess(`${correctedFilename} downloaded`);
    } catch (err) {
      setUploadError(err instanceof ApiError ? err.message : 'Failed to download document');
    } finally {
      setLoadingDocId(null);
    }
  };

  const handleValidateDocument = async () => {
    if (!selectedFile || !documentName.trim()) {
      setUploadError('Please select a file and enter a document name before validating');
      return;
    }

    try {
      setValidatingDocId('current-file'); // Use a marker for current file
      setUploadError('');
      setUploadSuccess('');
      setUploadWarning('');
      setValidationResult(null);

      const result = await apiService.validateDocument(selectedFile, documentName.trim());
      
      setValidationResult({
        documentId: 'current-file',
        status: result.status,
        errors: result.errors,
        notes: result.notes,
      });
      applyValidationFeedback(result);
    } catch (err) {
      setUploadError(err instanceof ApiError ? err.message : 'Failed to validate document');
    } finally {
      setValidatingDocId(null);
    }
  };

  const handlePreviewDocument = async (doc: DocumentWithReview) => {
    try {
      if (previewDoc) {
        URL.revokeObjectURL(previewDoc.url);
        setPreviewDoc(null);
      }
      setLoadingDocId(doc.id);
      setPreviewLoading(true);
      const blob = await apiService.downloadDocument(doc.id, true);
      const url = URL.createObjectURL(blob);
      setPreviewDoc({ url, name: doc.documentName, mimeType: doc.mimeType });
    } catch (err) {
      setUploadError(err instanceof ApiError ? err.message : 'Failed to preview document');
    } finally {
      setPreviewLoading(false);
      setLoadingDocId(null);
    }
  };

  const closePreview = () => {
    if (previewDoc) {
      URL.revokeObjectURL(previewDoc.url);
      setPreviewDoc(null);
    }
  };

  // Load documents on component mount
  useEffect(() => {
    fetchDocuments();
  }, []);

  // Background refresh when tab becomes active again
  useEffect(() => {
    if (!isActive || !hasLoadedOnce) return;

    const refreshInBackground = async () => {
      try {
        const docs = await apiService.listDocuments();
        setDocuments(docs);
      } catch {
        // Silently fail — user still sees previous data
      }
    };
    refreshInBackground();
  }, [isActive]);

  const getStatusBadge = (status?: DocumentReviewStatus) => {
    if (!status) return null;
    
    const statusConfig: Record<DocumentReviewStatus, { className: string; label: string }> = {
      'uploaded': { className: 'bg-blue-50 text-blue-700 border-blue-200', label: 'Uploaded' },
      'approved': { className: 'bg-green-50 text-green-700 border-green-200', label: 'Approved' },
      'rejected': { className: 'bg-red-50 text-red-700 border-red-200', label: 'Rejected' },
      'reuploaded': { className: 'bg-orange-50 text-orange-700 border-orange-200', label: 'Re-uploaded' },
      'expired': { className: 'bg-gray-50 text-gray-700 border-gray-200', label: 'Expired' },
    };
    
    const config = statusConfig[status] || { className: 'bg-gray-50 text-gray-700 border-gray-200', label: status };
    
    return (
      <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-lg border ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const openFeedbackPanel = (doc: DocumentWithReview) => {
    setFeedbackDoc(doc);
    setFeedbackStatus('rejected');
    setFeedbackComment('');
    setFeedbackError('');
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackDoc) return;

    if (feedbackStatus === 'rejected' && !feedbackComment.trim()) {
      setFeedbackError('A comment is required when rejecting a document');
      return;
    }

    try {
      setFeedbackSubmitting(true);
      setFeedbackError('');
      await apiService.reviewDocument(
        feedbackDoc.id,
        feedbackStatus,
        feedbackComment.trim() || undefined
      );
      setFeedbackDoc(null);
      await fetchDocuments();
    } catch (err) {
      setFeedbackError(err instanceof ApiError ? err.message : 'Failed to submit review');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Upload Document Side Panel */}
      <Dialog open={showUploadDialog} onOpenChange={(open) => {
        setShowUploadDialog(open);
        if (!open) {
          setDocumentName('');
          setSelectedFile(null);
          setUploadError('');
          setUploadSuccess('');
          setUploadWarning('');
          setValidationResult(null);
        }
      }}>
        <DialogContent
          className="!h-screen !max-w-none !translate-x-0 !translate-y-0 !top-0 !left-auto !right-0 !rounded-none !data-[state=closed]:slide-out-to-right !data-[state=open]:slide-in-from-right !flex !flex-col !justify-start !items-start !p-0 !gap-0 [&>button]:!right-6 [&>button]:!top-6"
          style={{ width: '840px' }}
        >
          <DialogHeader
            className="flex-shrink-0 w-full space-y-0"
            style={{ paddingLeft: '24px', paddingRight: '72px', paddingTop: '24px', paddingBottom: '16px' }}
          >
            <DialogTitle className="text-xl font-semibold">Upload Document</DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto w-full" style={{ padding: '0 24px' }}>
            <div className="space-y-6">
              {/* Document Type */}
              <div className="space-y-2">
                <label htmlFor="documentName" className="text-sm font-semibold">
                  Document Type
                </label>
                <Select
                  value={documentName}
                  onValueChange={setDocumentName}
                  disabled={isUploading}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a document type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SSN">SSN</SelectItem>
                    <SelectItem value="Government Issued ID">Government Issued ID</SelectItem>
                    <SelectItem value="Proof of Address">Proof of Address</SelectItem>
                    <SelectItem value="Name Change Document">Name Change Document</SelectItem>
                    <SelectItem value="Settlement Agreement">Settlement Agreement</SelectItem>
                    <SelectItem value="Payment Confirmation">Payment Confirmation</SelectItem>
                    <SelectItem value="Account Statement">Account Statement</SelectItem>
                    <SelectItem value="Court Document">Court Document</SelectItem>
                    <SelectItem value="Identity Theft Report">Identity Theft Report</SelectItem>
                    <SelectItem value="Police Report">Police Report</SelectItem>
                    <SelectItem value="Fraud Affidavit">Fraud Affidavit</SelectItem>
                    <SelectItem value="Other Documents">Other Documents</SelectItem>
                    <SelectItem value="Dispute Signature">Dispute Signature</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Upload Document */}
              <div className="space-y-2">
                <label className="text-sm font-semibold">
                  Upload Document
                </label>
                {!selectedFile ? (
                  <div 
                    className={`border-2 border-dashed rounded-md h-[96px] flex flex-col items-center justify-center text-center bg-white transition-colors ${
                      isDragging 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-300'
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <p className="text-sm text-gray-500 mb-3">
                      Upload a new document (PDF, JPG/JPEG up to 2 MB)
                    </p>
                    <Input
                      id="file-upload"
                      type="file"
                      onChange={handleFileUpload}
                      disabled={isUploading || validatingDocId === 'current-file' || !documentName.trim()}
                      accept=".jpg,.jpeg,.pdf"
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      disabled={isUploading || validatingDocId === 'current-file' || !documentName.trim()}
                      onClick={() => document.getElementById('file-upload')?.click()}
                      className="inline-flex items-center gap-2"
                    >
                      <Upload className="h-4 w-4" />
                      Upload
                    </Button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-blue-500 bg-blue-50 rounded-md p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded border border-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden cursor-pointer hover:opacity-80 transition-opacity bg-white"
                          onClick={() => {
                            const url = URL.createObjectURL(selectedFile);
                            setPreviewDoc({ url, name: selectedFile.name, mimeType: selectedFile.type });
                          }}
                        >
                          {selectedFile.type === 'application/pdf' ? (
                            <File className="h-5 w-5 text-red-500" />
                          ) : (
                            <img
                              src={URL.createObjectURL(selectedFile)}
                              alt={selectedFile.name}
                              className="w-full h-full object-cover"
                            />
                          )}
                        </div>
                        <div>
                          <button
                            type="button"
                            className="text-sm font-medium text-blue-600 hover:text-blue-700 underline text-left"
                            onClick={() => {
                              const url = URL.createObjectURL(selectedFile);
                              setPreviewDoc({ url, name: selectedFile.name, mimeType: selectedFile.type });
                            }}
                          >
                            {selectedFile.name}
                          </button>
                          <p className="text-xs text-gray-500">
                            {(selectedFile.size / 1024).toFixed(1)} KB • {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedFile(null);
                          setUploadSuccess('');
                          setUploadWarning('');
                          setValidationResult(null);
                          const fileInput = document.getElementById('file-upload') as HTMLInputElement;
                          if (fileInput) fileInput.value = '';
                        }}
                        className="h-8 w-8 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Validation Status / Result */}
              {validatingDocId === 'current-file' ? (
                <div className="flex items-center gap-2 text-purple-600">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <p className="text-sm font-medium">Wait a second, AI checking the document ...</p>
                </div>
              ) : validationResult && validationResult.documentId === 'current-file' ? (
                validationResult.status === 'VALID' && validationResult.errors.length === 0 ? (
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-base text-gray-900">File is valid.</p>
                      {validationResult.notes && (
                        <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{validationResult.notes}</p>
                      )}
                    </div>
                  </div>
                ) : validationResult.status === 'VALID' ? (
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-6 w-6 text-orange-500 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-base text-gray-900 mb-2">File is valid with warnings.</p>
                      <ul className="space-y-1 text-sm text-gray-600">
                        {validationResult.errors.map((warning, index) => (
                          <li key={index}>• {warning}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-6 w-6 text-orange-500 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-base text-gray-900 mb-2">File isn't valid.</p>
                      <ul className="space-y-1 text-sm text-gray-600">
                        {validationResult.errors.map((error, index) => (
                          <li key={index}>• {error}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )
              ) : selectedFile ? null : (
                <div className="flex items-start gap-2">
                  <Info className="h-5 w-5 text-gray-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-sm text-gray-900">Please make sure that:</p>
                    <ul className="mt-2 space-y-1 text-sm text-gray-600">
                      <li>• All 4 corners visible</li>
                      <li>• Not a screenshot</li>
                      <li>• Not expired</li>
                      <li>• Correct document type</li>
                      <li>• Name matches</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Status Messages */}
              {uploadError && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                  <p className="text-sm text-destructive whitespace-pre-wrap">{uploadError}</p>
                </div>
              )}

              {uploadSuccess && (
                <p className="text-sm text-green-600 font-medium">{uploadSuccess}</p>
              )}
              {uploadWarning && (
                <p className="text-sm text-orange-600 font-medium">{uploadWarning}</p>
              )}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex-shrink-0 w-full border-t" style={{ padding: '16px 24px' }}>
            <div className="flex justify-end">
              <Button 
                onClick={handleUploadClick}
                disabled={isUploading || validatingDocId === 'current-file' || !selectedFile || !documentName.trim()} 
                className="bg-gray-600 hover:bg-gray-700 text-white px-8"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Uploading...
                  </>
                ) : (
                  'Add & Save'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Documents List */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">Documents</h2>
          <Button 
            className="h-9 bg-black hover:bg-gray-800 text-white"
            onClick={() => setShowUploadDialog(true)}
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload Document
          </Button>
        </div>
        
        {isLoading ? (
          <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Document Type</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Document Name</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Size</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Uploaded</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Expiration Date</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Status</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
              </table>
            </div>
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          </div>
        ) : (
          <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
            {documentsWithReviews.length > 0 ? (
              <Table>
                <TableHeader className="[&_tr]:border-b-0">
                  {/* Column Headers */}
                  <TableRow className="border-b border-b-gray-200 bg-white">
                    <TableHead className="pl-4 pr-2 py-3 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>
                      Document Type
                    </TableHead>
                    <TableHead className="px-2 py-3 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>
                      Document Name
                    </TableHead>
                    <TableHead className="px-2 py-3 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>
                      Size
                    </TableHead>
                    <TableHead className="px-2 py-3 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>
                      <div className="flex items-center gap-1 cursor-pointer">
                        Uploaded
                        <ArrowUpDown className="h-3 w-3" />
                      </div>
                    </TableHead>
                    <TableHead className="px-2 py-3 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>
                      <div className="flex items-center gap-1 cursor-pointer">
                        Expiration Date
                        <ArrowUpDown className="h-3 w-3" />
                      </div>
                    </TableHead>
                    <TableHead className="px-2 py-3 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>
                      Status
                    </TableHead>
                    <TableHead className="px-2 py-3 text-sm font-medium" style={{ color: '#737373', fontWeight: 500 }}>
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documentsWithReviews.map((document, index) => {
                    const openComments = (document.reviewComments || []).filter(c => c.status === 'open');
                    const closedComments = (document.reviewComments || []).filter(c => c.status === 'closed');
                    const hasComments = (document.reviewComments || []).length > 0;
                    const isExpanded = expandedComments[document.id];

                    return (
                      <React.Fragment key={document.id}>
                        <TableRow 
                          className={`border-b border-gray-200 ${index === 0 ? 'border-t border-t-gray-200' : ''} last:border-b-0 hover:bg-gray-50/50 transition-colors ${hasComments ? 'cursor-pointer' : ''}`}
                          onClick={hasComments ? () => setExpandedComments(prev => ({ ...prev, [document.id]: !prev[document.id] })) : undefined}
                        >
                          <TableCell className="pl-4 pr-2 py-4 font-medium text-gray-900">
                            {document.documentName}
                          </TableCell>
                          <TableCell className="px-2 py-4 text-sm text-gray-900">
                            {document.documentName}.{document.mimeType.split('/')[1]}
                          </TableCell>
                          <TableCell className="px-2 py-4 text-sm text-gray-900">
                            {formatFileSize(document.fileSize)}
                          </TableCell>
                          <TableCell className="px-2 py-4 text-sm text-gray-900">
                            {formatDate(document.createdAt)}
                          </TableCell>
                          <TableCell className="px-2 py-4 text-sm text-gray-900">
                            {document.expirationDate ? formatDate(document.expirationDate) : '-'}
                          </TableCell>
                          <TableCell className="px-2 py-4">
                            <div className="flex items-center gap-2">
                              {getStatusBadge(document.reviewStatus)}
                              {hasComments && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setExpandedComments(prev => ({ ...prev, [document.id]: !prev[document.id] })); }}
                                  className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                                  aria-label="Toggle review comments"
                                >
                                  <MessageSquare className="h-3.5 w-3.5" />
                                  {openComments.length > 0 && (
                                    <span className="text-red-600 font-medium">{openComments.length}</span>
                                  )}
                                  {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                                </button>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="px-2 py-4" onClick={(e) => e.stopPropagation()}>
                            {loadingDocId === document.id ? (
                              <div className="h-8 w-8 flex items-center justify-center">
                                <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
                              </div>
                            ) : (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="sm" variant="ghost" className="h-8 w-8 p-0" aria-label="Document actions">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handlePreviewDocument(document)}>
                                  <Eye className="h-4 w-4 mr-2" />
                                  Preview
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDownloadDocument(document)}>
                                  <Download className="h-4 w-4 mr-2" />
                                  Download
                                </DropdownMenuItem>
                                {document.reviewStatus === 'rejected' && (
                                  <DropdownMenuItem onClick={() => {
                                    setDocumentName(document.documentName);
                                    setShowUploadDialog(true);
                                  }}>
                                    <Upload className="h-4 w-4 mr-2" />
                                    Re-upload
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => openFeedbackPanel(document)}>
                                  <MessageSquare className="h-4 w-4 mr-2" />
                                  Leave Feedback
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleDeleteDocument(document.id)} className="text-red-600 focus:text-red-600">
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                            )}
                          </TableCell>
                        </TableRow>
                        {/* Review Comments Row */}
                        {isExpanded && hasComments && (
                          <TableRow className="bg-gray-50/50 border-b border-gray-200">
                            <TableCell colSpan={7} className="px-4 py-3">
                              <div className="space-y-3">
                                {openComments.length > 0 && (
                                  <div>
                                    <p className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-2">Open Comments</p>
                                    <div className="space-y-2">
                                      {openComments.map((comment) => (
                                        <div key={comment.id} className="p-3 rounded-lg border border-red-200 bg-red-50/50">
                                          <p className="text-sm text-gray-900">{comment.comment}</p>
                                          <p className="text-xs text-gray-500 mt-1">
                                            {comment.reviewer_id === 'system' ? 'System' : 'Reviewer'} • {new Date(comment.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                          </p>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {closedComments.length > 0 && (
                                  <div>
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Resolved Comments</p>
                                    <div className="space-y-2">
                                      {closedComments.map((comment) => (
                                        <div key={comment.id} className="p-3 rounded-lg border border-gray-200 bg-white">
                                          <p className="text-sm text-gray-600">{comment.comment}</p>
                                          <p className="text-xs text-gray-400 mt-1">
                                            {comment.reviewer_id === 'system' ? 'System' : 'Reviewer'} • {new Date(comment.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                            {comment.closed_at && (
                                              <> • Closed {new Date(comment.closed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</>
                                            )}
                                          </p>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Document Type</th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Document Name</th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Size</th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Uploaded</th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Expiration Date</th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Status</th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Actions</th>
                      </tr>
                    </thead>
                  </table>
                </div>
                <div className="text-center py-16">
                  <div className="flex items-center justify-center mx-auto mb-4 bg-gray-100" style={{ width: '40px', height: '40px', borderRadius: '8px' }}>
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Documents Yet</h3>
                  <p className="text-sm text-gray-500">
                    You haven't uploaded any documents yet. Upload documents to support your disputes.
                  </p>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Document Preview Sidebar */}
      <Dialog open={!!previewDoc} onOpenChange={() => closePreview()}>
        <DialogContent
          className="!h-screen !max-w-none !translate-x-0 !translate-y-0 !top-0 !left-auto !right-0 !rounded-none !data-[state=closed]:slide-out-to-right !data-[state=open]:slide-in-from-right !flex !flex-col !justify-start !items-start !p-0 !gap-0 [&>button]:!right-6 [&>button]:!top-6"
          style={{ width: '1100px' }}
        >
          <DialogHeader
            className="flex-shrink-0 w-full space-y-0"
            style={{ paddingLeft: '24px', paddingRight: '72px', paddingTop: '24px', paddingBottom: '16px' }}
          >
            <DialogTitle className="text-lg font-semibold">{previewDoc?.name}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto w-full bg-gray-50" style={{ padding: '0 24px 24px' }}>
            {previewLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : previewDoc?.mimeType === 'application/pdf' ? (
              <iframe
                src={previewDoc.url}
                title={previewDoc.name}
                className="w-full h-full rounded border border-gray-200"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <img
                  src={previewDoc?.url}
                  alt={previewDoc?.name}
                  className="max-w-full max-h-full object-contain rounded"
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Leave Feedback Sidebar */}
      <Dialog open={!!feedbackDoc} onOpenChange={(open) => { if (!open) setFeedbackDoc(null); }}>
        <DialogContent
          className="!h-screen !max-w-none !translate-x-0 !translate-y-0 !top-0 !left-auto !right-0 !rounded-none !data-[state=closed]:slide-out-to-right !data-[state=open]:slide-in-from-right !flex !flex-col !justify-start !items-start !p-0 !gap-0 [&>button]:!right-6 [&>button]:!top-6"
          style={{ width: '840px' }}
        >
          <DialogHeader
            className="flex-shrink-0 w-full space-y-0"
            style={{ paddingLeft: '24px', paddingRight: '72px', paddingTop: '24px', paddingBottom: '16px' }}
          >
            <DialogTitle className="text-xl font-semibold">Leave Feedback</DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto w-full" style={{ padding: '0 24px' }}>
            <div className="space-y-6">
              {/* Document Info */}
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-3">
                  <File className="h-5 w-5 text-gray-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{feedbackDoc?.documentName}</p>
                    <p className="text-xs text-gray-500">
                      {feedbackDoc && formatFileSize(feedbackDoc.fileSize)} • v{feedbackDoc?.version} • {feedbackDoc && formatDate(feedbackDoc.createdAt)}
                    </p>
                  </div>
                  {feedbackDoc?.reviewStatus && getStatusBadge(feedbackDoc.reviewStatus)}
                </div>
              </div>

              {/* Existing Review Comments */}
              {feedbackDoc?.reviewComments && feedbackDoc.reviewComments.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-gray-900">Comment History</p>
                  <div className="space-y-2">
                    {feedbackDoc.reviewComments.map((comment) => (
                      <div key={comment.id} className={`p-3 rounded-lg border ${comment.status === 'open' ? 'border-red-200 bg-red-50/50' : 'border-gray-200 bg-white'}`}>
                        <p className="text-sm text-gray-900">{comment.comment}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {comment.reviewer_id === 'system' ? 'System' : 'Reviewer'} • {new Date(comment.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          {comment.status === 'closed' && comment.closed_at && (
                            <> • Closed {new Date(comment.closed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</>
                          )}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Decision */}
              <div className="space-y-2">
                <p className="text-sm font-semibold text-gray-900">Decision</p>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setFeedbackStatus('approved')}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 text-sm font-medium transition-colors ${
                      feedbackStatus === 'approved'
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <CheckCircle2 className="h-5 w-5 mx-auto mb-1" />
                    Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => setFeedbackStatus('rejected')}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 text-sm font-medium transition-colors ${
                      feedbackStatus === 'rejected'
                        ? 'border-red-500 bg-red-50 text-red-700'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <AlertCircle className="h-5 w-5 mx-auto mb-1" />
                    Reject
                  </button>
                </div>
              </div>

              {/* Comment */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-900">
                  Comment {feedbackStatus === 'rejected' && <span className="text-red-500">*</span>}
                </label>
                <Textarea
                  value={feedbackComment}
                  onChange={(e) => setFeedbackComment(e.target.value)}
                  placeholder={feedbackStatus === 'rejected' ? 'Explain why this document is being rejected...' : 'Optional comment...'}
                  className="min-h-[120px] resize-none"
                />
              </div>

              {feedbackError && (
                <p className="text-sm text-red-600">{feedbackError}</p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 w-full border-t" style={{ padding: '16px 24px' }}>
            <div className="flex items-center justify-between gap-4">
              <Button variant="outline" onClick={() => setFeedbackDoc(null)} disabled={feedbackSubmitting}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmitFeedback}
                disabled={feedbackSubmitting || (feedbackStatus === 'rejected' && !feedbackComment.trim())}
                className={feedbackStatus === 'approved' ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}
              >
                {feedbackSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Submitting...
                  </>
                ) : feedbackStatus === 'approved' ? (
                  'Approve Document'
                ) : (
                  'Reject Document'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}

